import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import EntityPreviewModal from '../components/EntityPreviewModal'
import { useCampaign } from './CampaignContext'
import { useAuth } from './AuthContext'

// Backs the [[wiki-link]] feature: builds a name -> entity registry from all
// linkable content so MarkdownText can turn [[Bern Rimewave]] into a click.
//
// Clicking a link opens a read-only PREVIEW stacked on top of the current view
// (it does not navigate away or close an open detail modal). Links inside a
// preview push another preview on top, so the user can drill into related
// entities and back out without losing their place. An "Open full view" button
// in the preview performs the actual tab navigation when they want it.
//
// Fog of war is inherited for free — the API already filters is_visible=0 rows
// out of player responses, so a player can't resolve (or preview) a hidden
// entity; the link renders as plain text instead.
//
// One exception we handle explicitly: when an *admin* uses "view as player",
// their session token still returns hidden rows from the API (the toggle is
// client-side only). So we filter is_visible=0 rows out of the registry here
// when the effective canEdit is false, to faithfully reproduce the player view.
const LinkContext = createContext(null)

// Order matters: later sources win on name collisions. NPCs are the most
// commonly referenced, so they're last (override locations/items/etc.).
const SOURCES = [
  { url: '/api/sessions', type: 'session', nameKey: 'title', module: 'sessions' },
  { url: '/api/quests', type: 'quest', nameKey: 'title', module: 'quests' },
  { url: '/api/items', type: 'item', nameKey: 'name', module: 'items' },
  { url: '/api/locations', type: 'location', nameKey: 'name', module: 'locations' },
  { url: '/api/roster', type: 'character', nameKey: 'character_name', module: 'character' },
  { url: '/api/npcs', type: 'npc', nameKey: 'name', module: 'npcs' },
]

export function LinkProvider({ children, onOpenEntity }) {
  const { canEdit, isModuleEnabled } = useCampaign()
  const { authEpoch } = useAuth()
  const [registry, setRegistry] = useState(() => new Map()) // name -> { type, id, label }
  const [entitiesById, setEntitiesById] = useState(() => new Map()) // `${type}:${id}` -> full row
  const [previewStack, setPreviewStack] = useState([])

  // Disabled modules must not contribute any names/entities to the registry:
  // no request, no results. Preserve SOURCES order — later entries still win
  // on collisions among whatever remains.
  const sources = useMemo(
    () => SOURCES.filter((s) => s.module === undefined || isModuleEnabled(s.module)),
    [isModuleEnabled]
  )

  const buildRegistry = useCallback(async () => {
    const rowsBySource = await Promise.all(
      sources.map((s) =>
        fetch(s.url)
          .then((r) => (r.ok ? r.json() : []))
          .catch(() => [])
      )
    )

    const byName = new Map()
    const byId = new Map()
    rowsBySource.forEach((rows, i) => {
      if (!Array.isArray(rows)) return
      const { type, nameKey } = sources[i]
      rows.forEach((row) => {
        // Mirror server-side fog of war for an admin previewing as a player:
        // hidden entities must not be resolvable/previewable. (Roster rows have
        // no is_visible field, so they're always public — never filtered.)
        if (!canEdit && row.is_visible === 0) return
        const name = row?.[nameKey]
        if (!name) return
        byName.set(String(name).trim().toLowerCase(), { type, id: row.id, label: name })
        byId.set(`${type}:${row.id}`, { ...row, type })
      })
    })
    setRegistry(byName)
    setEntitiesById(byId)
    // authEpoch: see the identical comment in components/tabs/ItemsTab.jsx —
    // otherwise a session that expired while the registry was already built
    // leaves every [[wiki-link]] silently unresolved until something else
    // happens to remount this provider.
  }, [sources, canEdit, authEpoch])

  useEffect(() => {
    buildRegistry()
  }, [buildRegistry])

  const resolveLink = useCallback(
    (name) => (name ? registry.get(String(name).trim().toLowerCase()) || null : null),
    [registry]
  )

  // Called by a clicked [[wiki-link]]: push the entity onto the preview stack.
  const openEntity = useCallback(
    (type, id) => {
      const entity = entitiesById.get(`${type}:${id}`)
      if (entity) setPreviewStack((stack) => [...stack, entity])
    },
    [entitiesById]
  )

  const closeTopPreview = useCallback(() => setPreviewStack((stack) => stack.slice(0, -1)), [])
  const closeAllPreviews = useCallback(() => setPreviewStack([]), [])

  // "Open full view" — navigate to the entity's tab and dismiss the previews.
  const navigateToEntity = useCallback(
    (type, id) => {
      closeAllPreviews()
      onOpenEntity?.(type, id)
    },
    [onOpenEntity, closeAllPreviews]
  )

  // ESC pops the top preview. Use the capture phase + stopPropagation so the
  // underlying detail modal's own ESC handler doesn't also fire and close it.
  useEffect(() => {
    if (previewStack.length === 0) return
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        closeTopPreview()
      }
    }
    window.addEventListener('keydown', handleEscape, true)
    return () => window.removeEventListener('keydown', handleEscape, true)
  }, [previewStack.length, closeTopPreview])

  const value = { resolveLink, openEntity, navigateToEntity, refreshLinks: buildRegistry }

  return (
    <LinkContext.Provider value={value}>
      {children}
      {previewStack.map((entity, i) => (
        <EntityPreviewModal
          key={`${entity.type}:${entity.id}:${i}`}
          entity={entity}
          depth={i}
          onClose={closeTopPreview}
          onOpenFull={() => navigateToEntity(entity.type, entity.id)}
        />
      ))}
    </LinkContext.Provider>
  )
}

// Safe to call outside a provider (e.g. FeywildPage, which renders MarkdownText
// but has no LinkProvider): returns inert defaults so [[links]] degrade to text.
export function useLink() {
  return (
    useContext(LinkContext) || {
      resolveLink: () => null,
      openEntity: () => {},
      navigateToEntity: () => {},
      refreshLinks: () => {},
    }
  )
}
